import { type } from 'arktype'

import { newAmount, newFinancialRate } from '@orus.eu/amount'
import type { ParameterlessTranslationKey } from '@orus.eu/translations'

const nlAovqlAow = 'AOW' as const
export const nlAovqlAowSchema = type('===', nlAovqlAow)

/** The end age a cover until retirement runs to for Quantum Leben, above today's legal AOW age of 67. */
export const nlAovqlAowAge = 70 as const
export const nlAovqlMinAge = 18 as const
export const nlAovqlMaxAge = 67 as const
export const nlAovqlAgeSchema = type(`${nlAovqlMinAge} <= number.integer <= ${nlAovqlMaxAge}`)

export const nlAovqlIncapacityThresholds = ['P25', 'P35', 'P45', 'P55', 'P65', 'P80'] as const
export type NlAovqlIncapacityThreshold = (typeof nlAovqlIncapacityThresholds)[number]

export const nlAovqlMinMonthlyInsuredAmount = newAmount(1_000)
export const nlAovqlMaxMonthlyInsuredAmount = newAmount(10_000)
/** The share of the yearly income the monthly insured amount covers at most, spread over twelve months. */
export const nlAovqlMaxInsuredIncomeRate = newFinancialRate('0.8')

export const nlAovqlWaitingTimesInMonths = [1, 3, 6, 12, 24] as const
export type NlAovqlWaitingTimeInMonths = (typeof nlAovqlWaitingTimesInMonths)[number]

export const nlAovqlWaitingTimeLabelKeys = {
  1: 'nl_aovql_waiting_time_1_month',
  3: 'nl_aovql_waiting_time_3_months',
  6: 'nl_aovql_waiting_time_6_months',
  12: 'nl_aovql_waiting_time_12_months',
  24: 'nl_aovql_waiting_time_24_months',
} as const satisfies Record<NlAovqlWaitingTimeInMonths, ParameterlessTranslationKey>

/** The waiting time in days, as the rater counts it. */
export const nlAovqlWaitingTimesInDays: Record<NlAovqlWaitingTimeInMonths, number> = {
  1: 30,
  3: 90,
  6: 180,
  12: 365,
  24: 730,
}

/** The provisional cover pays at most €50,000 a year, which the product sheet rounds to €4,167 a month. */
export const nlAovqlProvisionalCoverMaxMonthlyAmount = newAmount(4_167)

export const nlAovqlBenefitDurationEntries = [
  ['12-months', { labelKey: 'nl_aovql_benefit_duration_12_months' }],
  ['24-months', { labelKey: 'nl_aovql_benefit_duration_24_months' }],
  ['60-months', { labelKey: 'nl_aovql_benefit_duration_60_months' }],
  ['until-end-of-term', { labelKey: 'nl_aovql_benefit_duration_until_end_of_term' }],
] as const
export type NlAovqlBenefitDuration = (typeof nlAovqlBenefitDurationEntries)[number][0]

/** The rates the back office may pick for the inflation indexation. The funnel only offers the default. */
export const nlAovqlInflationIndexationRates = [
  newFinancialRate('0.005'),
  newFinancialRate('0.01'),
  newFinancialRate('0.015'),
  newFinancialRate('0.02'),
  newFinancialRate('0.025'),
  newFinancialRate('0.03'),
] as const

export const nlAovqlDefaultInflationIndexationRate = newFinancialRate('0.03')

export const nlAovqlCoverEndAgeSchema = type.or(
  { untilRetirement: 'true' },
  { untilRetirement: 'false', endAge: nlAovqlAgeSchema },
)
export type NlAovqlCoverEndAge = typeof nlAovqlCoverEndAgeSchema.infer

export function getNlAovqlCoverEndAgeInYears(coverEndAge: NlAovqlCoverEndAge): number {
  return coverEndAge.untilRetirement ? nlAovqlAowAge : coverEndAge.endAge
}

export const nlAovqlMinContractDurationInYears = 5

/** The lowest end age while the age at start is unknown. */
export const nlAovqlDefaultMinCoverEndAge = nlAovqlMinAge + nlAovqlMinContractDurationInYears
